import { useSyncExternalStore } from 'react';
import {
  isSamsungInternet,
  detectSamsungSitesMode,
} from '../utils/samsungBrowser';

export type SamsungSitesMode = 'dark-sites' | 'light-sites' | 'unknown';

type Listener = () => void;

let sitesMode: SamsungSitesMode = 'unknown';
let started = false;
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => l());
}

async function probe() {
  if (!isSamsungInternet()) {
    if (sitesMode !== 'unknown') {
      sitesMode = 'unknown';
      emit();
    }
    return;
  }

  const mode = await detectSamsungSitesMode();
  if (mode !== sitesMode) {
    sitesMode = mode;
    emit();
  }
}

function startWatcher() {
  if (started || typeof window === 'undefined') return;
  started = true;

  if (!isSamsungInternet()) return;

  probe();
  const interval = window.setInterval(probe, 2000);

  const onRecheck = () => {
    if (document.visibilityState === 'visible') probe();
  };
  document.addEventListener('visibilitychange', onRecheck);
  window.addEventListener('focus', onRecheck);

  return () => {
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', onRecheck);
    window.removeEventListener('focus', onRecheck);
  };
}

function subscribe(listener: Listener) {
  startWatcher();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): SamsungSitesMode {
  return sitesMode;
}

function getServerSnapshot(): SamsungSitesMode {
  return 'unknown';
}

export function useSamsungSitesMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    sitesMode: mode,
    isSamsungDarkSites: mode === 'dark-sites',
  };
}
